import { NavigationMenuChevron } from "@templates/design-system";
import { cn } from "@newxsystems/shared";
import { MegaMenuLink } from "../MegaMenuLink/MegaMenuLink";
import type { IMegaMenuResponsiveItemProps } from "./MegaMenuResponsiveItem.props";

const itemControlClasses =
  "flex min-h-12 w-full min-w-0 items-center justify-between gap-3 border-b border-surface-300 px-1 py-3 text-left text-base/[1.35] font-medium transition-colors duration-200 hover:text-electric-blue focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-ring";

const panelClasses =
  "grid overflow-hidden transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none";

export const MegaMenuResponsiveItem = ({
  isOpen,
  item,
  itemKey,
  onNavigate,
  onToggle,
  panelId,
  triggerId,
}: IMegaMenuResponsiveItemProps) => {
  if (item.blockType === "megaMenuDirectLink") {
    return (
      <li className="min-w-0">
        <MegaMenuLink className={itemControlClasses} link={item.link} onActivate={onNavigate} />
      </li>
    );
  }

  return (
    <li className="min-w-0">
      <button
        aria-controls={panelId}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={cn(itemControlClasses, "group cursor-pointer", isOpen && "text-electric-blue")}
        data-state={isOpen ? "open" : "closed"}
        id={triggerId}
        onClick={() => onToggle(itemKey)}
        type="button"
      >
        <span className="min-w-0 [overflow-wrap:anywhere]">{item.label}</span>
        <NavigationMenuChevron />
      </button>
      <div
        aria-hidden={!isOpen}
        aria-labelledby={triggerId}
        className={cn(
          panelClasses,
          isOpen ? "grid-rows-[1fr] opacity-100" : "pointer-events-none grid-rows-[0fr] opacity-0",
        )}
        data-state={isOpen ? "open" : "closed"}
        id={panelId}
        inert={!isOpen}
        role="region"
      >
        <div className="min-h-0 overflow-hidden">
          {item.blockType === "megaMenuDropdown" ? (
            <ul className="list-none border-b border-surface-300 pb-2 pl-3">
              {item.links.map((link, index) => (
                <li key={`${link.href}-${index}`}>
                  <MegaMenuLink
                    className="block border-b border-surface-300 px-3 py-3 text-base/[1.35] transition-colors duration-200 hover:text-electric-blue focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-ring"
                    link={link}
                    onActivate={onNavigate}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="grid gap-6 border-b border-surface-300 px-3 py-5 sm:grid-cols-2">
              {item.groupItems.map((group, groupIndex) => {
                const headingId = `${panelId}-group-${groupIndex}`;

                return (
                  <section aria-labelledby={headingId} key={`${group.header}-${groupIndex}`}>
                    <h3 className="mb-3 text-[17px]/[1.35] font-medium" id={headingId}>
                      {group.header}
                    </h3>
                    <ul
                      aria-labelledby={headingId}
                      className="list-none border-t border-foreground"
                    >
                      {group.links.map((link, linkIndex) => (
                        <li
                          className="border-b border-surface-300"
                          key={`${link.href}-${linkIndex}`}
                        >
                          <MegaMenuLink
                            className="block px-3 py-3 text-base/[1.35] transition-colors duration-200 hover:bg-surface-200 hover:text-electric-blue focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-ring"
                            link={link}
                            onActivate={onNavigate}
                          />
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

MegaMenuResponsiveItem.displayName = "MegaMenu::MegaMenuResponsiveItem";
